/**
 * InsightsCard — Smart Portfolio Insights widget for the Dashboard.
 *
 * Fetches /api/insights/daily (cached in Redis) and renders prioritised,
 * proactive insights about the real portfolio — ranked by £ at stake.
 *
 * Each insight (matching the backend payload):
 *   - category: market | tax | risk | advisor | goal | cash
 *   - priority: high | medium | low
 *   - title / message
 *   - impact_gbp: £ saveable / at stake (drives the headline + per-row badge)
 *   - action: optional terminal command (e.g. "/tax-loss")
 */

import { useEffect, useState, useCallback } from 'react'
import { Lightbulb, AlertTriangle, Info, Zap, RefreshCw, ArrowRight, ChevronDown, ChevronUp, TrendingUp, Receipt, ShieldAlert, Target, Wallet } from 'lucide-react'
import { getDailyInsights } from '@/lib/api'
import type { PortfolioInsight } from '@/lib/api'

// ── Category → icon / colour ──────────────────────────────────────────────────

const CATEGORY_CONFIG: Record<string, { icon: React.ReactNode; badgeClass: string; dotClass: string }> = {
  tax:      { icon: <Receipt size={13} className="shrink-0" />,      badgeClass: 'bg-emerald-900/40 text-emerald-300', dotClass: 'bg-emerald-400' },
  risk:     { icon: <ShieldAlert size={13} className="shrink-0" />,  badgeClass: 'bg-rose-900/40 text-rose-300',       dotClass: 'bg-rose-400' },
  market:   { icon: <TrendingUp size={13} className="shrink-0" />,   badgeClass: 'bg-blue-900/40 text-blue-300',       dotClass: 'bg-blue-400' },
  goal:     { icon: <Target size={13} className="shrink-0" />,       badgeClass: 'bg-violet-900/40 text-violet-300',   dotClass: 'bg-violet-400' },
  cash:     { icon: <Wallet size={13} className="shrink-0" />,       badgeClass: 'bg-amber-900/40 text-amber-300',     dotClass: 'bg-amber-400' },
  advisor:  { icon: <Zap size={13} className="shrink-0" />,          badgeClass: 'bg-slate-700/60 text-slate-300',     dotClass: 'bg-slate-400' },
}
const FALLBACK_CFG = { icon: <Info size={13} className="shrink-0" />, badgeClass: 'bg-slate-700/60 text-slate-300', dotClass: 'bg-slate-400' }

const PRIORITY_RING: Record<string, string> = {
  high:   'border-rose-500/50',
  medium: 'border-amber-500/40',
  low:    'border-slate-700/30',
}

function fmtGbp(v: number): string {
  if (!Number.isFinite(v) || v <= 0) return ''
  if (Math.abs(v) >= 1_000_000) return `£${(v / 1_000_000).toFixed(2)}M`
  if (Math.abs(v) >= 1_000) return `£${Math.round(v).toLocaleString()}`
  return `£${v.toFixed(0)}`
}

// ── InsightRow ────────────────────────────────────────────────────────────────

function InsightRow({ insight, onCommand }: {
  insight: PortfolioInsight
  onCommand?: (cmd: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const cfg = CATEGORY_CONFIG[insight.category] ?? FALLBACK_CFG
  const ring = PRIORITY_RING[insight.priority] ?? 'border-slate-700/30'
  const impact = fmtGbp(insight.impact_gbp ?? 0)

  return (
    <li className={`rounded-lg border ${ring} bg-surface/40 px-3 py-2.5 transition-colors hover:bg-surface-hover/40`}>
      <button
        type="button"
        className="w-full text-left flex items-start gap-2"
        onClick={() => setExpanded(e => !e)}
        aria-expanded={expanded}
      >
        <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${cfg.dotClass}`} aria-hidden />

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text-primary leading-snug">{insight.title}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded font-medium ${cfg.badgeClass}`}>
              {cfg.icon}
              {insight.category}
            </span>
            {impact && (
              <span
                className="inline-flex items-center text-xs px-1.5 py-0.5 rounded font-semibold bg-emerald-950/60 text-emerald-300 border border-emerald-700/40"
                title="Estimated £ at stake / saveable by acting"
              >
                {impact}
              </span>
            )}
          </div>
        </div>

        <span className="text-text-muted mt-0.5 shrink-0">
          {expanded ? <ChevronUp size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
        </span>
      </button>

      {expanded && (
        <div className="mt-2 pl-4 space-y-2">
          <p className="text-xs text-text-secondary leading-relaxed">{insight.message}</p>
          {/* Only render the action button when a handler exists — otherwise it
              would be a silent no-op that looks clickable but does nothing. */}
          {insight.action && onCommand && (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-xs text-blue-light hover:underline"
              onClick={e => { e.stopPropagation(); onCommand(insight.action!) }}
            >
              {insight.action} <ArrowRight size={11} />
            </button>
          )}
        </div>
      )}
    </li>
  )
}

// ── InsightsCard ──────────────────────────────────────────────────────────────

interface InsightsCardProps {
  onTerminalCommand?: (cmd: string) => void
}

export default function InsightsCard({ onTerminalCommand }: InsightsCardProps) {
  const [insights, setInsights] = useState<PortfolioInsight[]>([])
  const [totalImpact, setTotalImpact] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  const load = useCallback(async () => {
    try {
      const res = await getDailyInsights()
      setInsights(Array.isArray(res.insights) ? res.insights : [])
      setTotalImpact(res.total_impact_gbp ?? 0)
      setError(res.error ?? null)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load insights')
    }
  }, [])

  useEffect(() => { load().finally(() => setLoading(false)) }, [load])

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      const { refreshInsights } = await import('@/lib/api')
      const res = await refreshInsights()
      setInsights(Array.isArray(res.insights) ? res.insights : [])
      setTotalImpact(res.total_impact_gbp ?? 0)
      setError(res.error ?? null)
    } catch {
      await load()
    } finally {
      setRefreshing(false)
    }
  }

  const urgentCount = insights.filter(i => i.priority === 'high').length
  const headlineImpact = fmtGbp(totalImpact)

  return (
    <div className="card p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Lightbulb size={16} className="text-amber" aria-hidden />
          <h2 className="font-semibold text-text-primary">Smart Insights</h2>
          {!loading && urgentCount > 0 && (
            <span className="badge bg-rose-900/50 text-rose-300 text-xs font-semibold px-1.5 py-0.5 rounded">
              {urgentCount} urgent
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={loading || refreshing}
          className="btn-ghost p-1 rounded text-text-muted hover:text-text-primary"
          aria-label="Refresh insights"
          title="Force-refresh insights"
        >
          <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} aria-hidden />
        </button>
      </div>

      {/* "Money on the table" headline */}
      {!loading && !error && headlineImpact && (
        <div className="mb-3 rounded-lg border border-emerald-700/40 bg-emerald-950/40 px-3 py-2">
          <p className="text-xs text-emerald-300/80">Money on the table today</p>
          <p className="text-lg font-bold text-emerald-300 leading-tight">{headlineImpact}</p>
          <p className="text-[11px] text-emerald-300/60">est. tax saved + risk reduced if you act on the items below</p>
        </div>
      )}

      {/* Body */}
      {loading ? (
        <ul className="space-y-2" aria-busy="true" aria-label="Loading insights">
          {[...Array(4)].map((_, i) => (
            <li key={i} className="h-12 rounded-lg bg-surface-hover/40 animate-pulse" />
          ))}
        </ul>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-6 text-center gap-2">
          <AlertTriangle size={22} className="text-amber opacity-60" aria-hidden />
          <p className="text-sm text-text-muted">{error}</p>
          <button type="button" onClick={handleRefresh} className="text-xs text-blue-light hover:underline">
            Try again
          </button>
        </div>
      ) : insights.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-6 text-center gap-2">
          <Lightbulb size={22} className="text-text-muted opacity-30" aria-hidden />
          <p className="text-sm text-text-muted">No insights yet</p>
          <p className="text-xs text-text-muted opacity-60">
            Run <code className="bg-surface px-1 rounded">/insights</code> in the terminal
          </p>
        </div>
      ) : (
        <ul className="space-y-2" aria-label="Portfolio insights">
          {insights.map(ins => (
            <InsightRow key={ins.id} insight={ins} onCommand={onTerminalCommand} />
          ))}
        </ul>
      )}
    </div>
  )
}
